import { useState } from 'react';
import type { SnapshotActionDef, SnapshotActionId, SnapshotRun } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { ApiError } from '../../api/client.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { DataState } from '../../components/ui/DataState.js';
import { formatDateTime } from '../../utils/date.js';
import { useDispatchSnapshotAction, useSnapshotRuns, useSnapshotStatus } from './api.js';

/** Tone for a workflow run's status/conclusion. */
function runTone(run: SnapshotRun): 'green' | 'amber' | 'red' | 'gray' {
  if (run.status !== 'completed') return 'amber';
  if (run.conclusion === 'success') return 'green';
  if (run.conclusion === 'failure' || run.conclusion === 'cancelled') return 'red';
  return 'gray';
}

function runLabel(run: SnapshotRun): string {
  if (run.status !== 'completed') return run.status === 'in_progress' ? 'Running' : 'Queued';
  return run.conclusion ? run.conclusion[0]!.toUpperCase() + run.conclusion.slice(1) : 'Done';
}

export function SnapshotManagementPage(): JSX.Element {
  const { isAdmin } = useAuth();
  const statusQuery = useSnapshotStatus(isAdmin);
  const runsQuery = useSnapshotRuns(isAdmin);
  const dispatch = useDispatchSnapshotAction();

  const [confirmAction, setConfirmAction] = useState<SnapshotActionDef | null>(null);
  const [notice, setNotice] = useState<{ url: string; text: string } | null>(null);

  const copy = (text: string): void => void navigator.clipboard?.writeText(text);

  const run = (action: SnapshotActionId): void => {
    setNotice(null);
    dispatch.mutate(action, {
      onSuccess: (res) => setNotice({ url: res.runsUrl, text: res.message }),
    });
  };

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="PSA Snapshot Management" />
        <Card>
          <p className="text-sm text-slate-600">
            You need administrator privileges to manage the sandbox snapshot.
          </p>
        </Card>
      </div>
    );
  }

  const status = statusQuery.data;
  const dispatchError =
    dispatch.error instanceof ApiError
      ? dispatch.error.message
      : dispatch.error
        ? 'Failed to start the action.'
        : null;

  return (
    <div>
      <PageHeader
        title="PSA Snapshot Management"
        description="Manage the disposable sandbox: refresh its data, redeploy its build, and open or close it to users. Privileged Azure work runs in GitHub Actions."
      />

      <div className="space-y-6">
        <Card>
          <DataState
            isLoading={statusQuery.isLoading}
            isError={statusQuery.isError}
            error={statusQuery.error}
            onRetry={() => void statusQuery.refetch()}
            isEmpty={false}
          >
            {status ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-slate-700">This deployment:</span>
                  <Badge tone={status.isSandbox ? 'blue' : 'green'}>
                    {status.isSandbox ? 'Sandbox' : 'Production'}
                  </Badge>
                  <span className="text-sm font-medium text-slate-700">Sandbox:</span>
                  <Badge tone={status.sandboxOnline ? 'green' : 'gray'}>
                    {status.sandboxOnline ? 'Online' : 'Offline'}
                  </Badge>
                  {!status.dispatchConfigured ? (
                    <Badge tone="amber">One-click actions not configured</Badge>
                  ) : null}
                </div>

                {status.isSandbox ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    You are viewing the <strong>sandbox</strong>. Snapshot management runs from
                    production — open the production app to refresh or control the sandbox.
                  </div>
                ) : null}

                <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <UrlRow label="Sandbox app" url={status.sandboxWebUrl} onCopy={copy} />
                  <UrlRow label="Sandbox API" url={status.sandboxApiUrl} onCopy={copy} />
                </dl>
              </div>
            ) : null}
          </DataState>
        </Card>

        {status && !status.isSandbox ? (
          <Card>
            <h3 className="mb-1 text-sm font-semibold text-slate-900">Actions</h3>
            <p className="mb-4 text-sm text-slate-600">
              {status.dispatchConfigured
                ? 'Each button dispatches a GitHub Actions workflow. Track progress under Recent activity.'
                : 'One-click actions are not configured (set GITHUB_ACTIONS_TOKEN). Run the manual command shown for each action instead.'}
            </p>

            {notice ? (
              <div className="mb-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                {notice.text}{' '}
                <a
                  href={notice.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline"
                >
                  View on GitHub ↗
                </a>
              </div>
            ) : null}
            {dispatchError ? (
              <div className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {dispatchError}
              </div>
            ) : null}

            <div className="divide-y divide-slate-100">
              {status.actions.map((action) => {
                const busy = dispatch.isPending && dispatch.variables === action.id;
                return (
                  <div
                    key={action.id}
                    className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900">{action.label}</span>
                        {action.destructive ? <Badge tone="red">Destructive</Badge> : null}
                      </div>
                      <p className="text-sm text-slate-600">{action.description}</p>
                      {!status.dispatchConfigured ? (
                        <code className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
                          {action.manualCommand}
                        </code>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {!status.dispatchConfigured ? (
                        <Button variant="secondary" onClick={() => copy(action.manualCommand)}>
                          Copy command
                        </Button>
                      ) : (
                        <Button
                          variant={action.destructive ? 'danger' : 'secondary'}
                          isLoading={busy}
                          onClick={() =>
                            action.destructive ? setConfirmAction(action) : run(action.id)
                          }
                        >
                          {action.label}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ) : null}

        {status?.dispatchConfigured ? (
          <Card>
            <h3 className="mb-3 text-sm font-semibold text-slate-900">Recent activity</h3>
            <DataState
              isLoading={runsQuery.isLoading}
              isError={runsQuery.isError}
              error={runsQuery.error}
              onRetry={() => void runsQuery.refetch()}
              isEmpty={(runsQuery.data ?? []).length === 0}
              emptyTitle="No recent runs"
              emptyDescription="Snapshot workflow runs will appear here."
            >
              <ul className="divide-y divide-slate-100">
                {(runsQuery.data ?? []).map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <div className="min-w-0">
                      <a
                        href={r.htmlUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-brand-600 hover:underline"
                      >
                        {r.name}
                      </a>
                      <span className="ml-2 text-slate-500">{formatDateTime(r.createdAt)}</span>
                    </div>
                    <Badge tone={runTone(r)}>{runLabel(r)}</Badge>
                  </li>
                ))}
              </ul>
            </DataState>
          </Card>
        ) : null}
      </div>

      <ConfirmDialog
        isOpen={confirmAction !== null}
        title={confirmAction?.label ?? ''}
        message={`${confirmAction?.description ?? ''} This cannot be undone.`}
        confirmLabel={confirmAction?.label ?? 'Confirm'}
        destructive
        isConfirming={dispatch.isPending}
        onConfirm={() => {
          if (confirmAction) run(confirmAction.id);
          setConfirmAction(null);
        }}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}

function UrlRow({
  label,
  url,
  onCopy,
}: {
  label: string;
  url: string | null;
  onCopy: (text: string) => void;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 flex items-center gap-2 text-sm">
        {url ? (
          <>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate font-medium text-brand-600 hover:underline"
            >
              {url}
            </a>
            <button
              type="button"
              onClick={() => onCopy(url)}
              className="shrink-0 rounded border border-slate-300 px-1.5 py-0.5 text-xs text-slate-600 hover:bg-slate-50"
            >
              Copy
            </button>
          </>
        ) : (
          <span className="text-slate-400">Not configured</span>
        )}
      </dd>
    </div>
  );
}
